import { useEffect, useState, useRef } from "react";
import DashLayout from "@/components/app/DashLayout";
import OfficeStaffShell from "@/components/app/OfficeStaffShell";
import SwipeableLeadCard from "@/components/app/SwipeableLeadCard";
import PullToRefresh from "@/components/app/PullToRefresh";
import LocationCard from "@/components/app/LocationCard";
import { ClipboardList, Phone, MessageSquare, CheckCircle2, IndianRupee, MapPin, Users2, ScrollText, Target, Trophy, UserCog, Lightbulb, Save, X, Landmark, PlusCircle, MinusCircle, GitBranch, ShieldCheck, Users, Hourglass, Building2, ChevronRight, MapPinned, Navigation, RefreshCw, ExternalLink, Ban, AlertTriangle, FolderSync, Pencil } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ReferralButton from "@/components/app/ReferralButton";
import UniversalLeadAdd from "@/components/app/UniversalLeadAdd";
import useLocations from "@/hooks/useLocations";
import { LoanIcon } from "@/lib/loanIcons";
import InBankPoolPage from "@/pages/shared/InBankPoolPage";
import MovingFilesView from "@/pages/telecaller/MovingFilesView";
import TelecallerHomeDashboard from "@/pages/telecaller/TelecallerHomeDashboard";
import PipelineTimeline from "@/components/app/PipelineTimeline";
import RejectionHistory from "@/components/app/RejectionHistory";
import CallTrackerProvider, { useCallTracker } from "@/components/app/CallTracker";
import TrackedCallButton from "@/components/app/TrackedCallButton";
import TelecallerCallStats from "@/components/app/TelecallerCallStats";
import LeadCallHistory from "@/components/app/LeadCallHistory";
import OverdueFollowupBanner from "@/components/app/OverdueFollowupBanner";
import LapSections, { computeLap, lapProgress, isLap, defaultHouseTypeFor } from "@/components/app/LapSections";
import TakeOverSections, { computeTakeover, takeoverProgress, isTakeOver } from "@/components/app/TakeOverSections";

const CALL_HINTS = [
  "Greet by name and identify yourself: “Hi {name}, calling from Shakthi Consultancy.”",
  "Confirm the exact loan they enquired about — amount, tenure, purpose.",
  "Verify income source & monthly take-home before promising anything.",
  "Ask for basic KYC readiness: PAN, Aadhaar, 3 months bank statement.",
  "If not interested right now — capture the reason + best time to call back.",
  "Never quote a fixed rate; say “our bank partner will finalise based on profile”.",
  "Log everything in Profile so the bank pool has full context.",
];

function ChatThread({ leadId, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/staff/leads/${leadId}/chat`);
      setMessages(r.data.messages || []);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (!leadId) return;
    setLoading(true); load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [leadId]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const r = await api.post(`/staff/leads/${leadId}/chat`, { text: t });
      setMessages(m => [...m, r.data.message]);
      setText("");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSending(false); }
  };

  const roleLabel = { office_staff: "Telecaller", bank_staff: "Bank", admin: "Admin" };
  return (
    <div className="flex flex-col h-72 sm:h-80" data-testid="chat-thread">
      <div className="flex-1 overflow-y-auto space-y-2 bg-slate-950/50 border border-white/5 rounded-lg p-3">
        {loading && <div className="text-center text-slate-500 text-xs py-4">Loading…</div>}
        {!loading && messages.length === 0 && (
          <div className="text-center text-slate-500 text-xs py-6" data-testid="chat-empty">
            No messages yet. Share a KYC note, callback plan, or ping the bank team here.
          </div>
        )}
        {messages.map(m => {
          const isMe = m.by_id === currentUserId;
          const isBank = m.by_role === "bank_staff";
          return (
            <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs ${isMe ? "bg-amber-500/20 border border-amber-500/30 text-amber-50" : isBank ? "bg-blue-500/10 border border-blue-500/30 text-blue-50" : "bg-slate-800 border border-white/5 text-slate-200"}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] uppercase tracking-widest font-bold">{m.by_name}</span>
                  <span className="text-[9px] uppercase tracking-widest opacity-60">{roleLabel[m.by_role] || m.by_role}</span>
                </div>
                <div className="whitespace-pre-wrap">{m.text}</div>
                <div className="text-[9px] opacity-50 mt-1">
                  {(() => { try { return new Date(m.at).toLocaleString("en-IN"); } catch { return m.at; } })()}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>
      <div className="mt-2 flex gap-2">
        <textarea
          data-testid="chat-input"
          rows={2}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500"
        />
        <Button
          data-testid="chat-send-btn"
          onClick={send}
          disabled={sending || !text.trim()}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-md self-stretch px-4 disabled:opacity-40"
        >
          <Save className="w-4 h-4"/>
        </Button>
      </div>
    </div>
  );
}

function ApplicantsEditor({ profile, setProfile }) {
  const applicants = Array.isArray(profile?.applicants) && profile.applicants.length
    ? profile.applicants
    : [emptyApplicant("")];
  const n = applicants.length;

  const setCount = (nv) => {
    const target = Math.max(1, Math.min(10, parseInt(nv || "1", 10)));
    const next = applicants.slice(0, target);
    while (next.length < target) next.push(emptyApplicant());
    setProfile(p => ({ ...(p || {}), applicants: next, num_applicants: target }));
  };

  const upd = (idx, key, val) => {
    const next = applicants.map((a, i) => i === idx ? { ...(a || {}), [key]: val } : (a || emptyApplicant()));
    setProfile(p => ({ ...(p || {}), applicants: next, num_applicants: next.length }));
  };

  const [openIdx, setOpenIdx] = useState({ 0: true });
  const toggleOpen = (i) => setOpenIdx(prev => ({ ...prev, [i]: !prev[i] }));

  const totalIncome = applicants.reduce((sum, a) => sum + (parseFloat((a && a.monthly_income) || 0) || 0), 0);

  return (
    <div data-testid="applicants-editor" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3 rounded-lg bg-slate-950/60 border border-slate-800 p-3">
        <div className="flex items-center gap-2">
          <Users2 className="w-4 h-4 text-amber-400"/>
          <Label className="text-[11px] uppercase tracking-widest text-slate-400">Number of Applicants</Label>
        </div>
        <select
          data-testid="profile-num-applicants"
          value={n}
          onChange={e => setCount(e.target.value)}
          className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-1.5 text-sm focus:border-amber-500 focus:outline-none"
        >
          {[1,2,3,4,5,6,7,8,9,10].map(i => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      {applicants.map((a, i) => {
        a = a || emptyApplicant();
        const isOpen = openIdx[i] !== false;
        return (
          <div key={i} data-testid={`applicant-card-${i}`} className="rounded-lg border border-slate-800 bg-slate-950/40 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleOpen(i)}
              data-testid={`applicant-toggle-${i}`}
              className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/5 transition-colors">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-300 text-[10px] font-bold">{i+1}</div>
                <div className="font-heading text-sm text-white">Applicant {i+1}</div>
                {a.full_name && <span className="text-[11px] text-slate-500 truncate max-w-[220px]">· {a.full_name}</span>}
              </div>
              <div className="flex items-center gap-2">
                {a.monthly_income && <span className="text-[10px] font-mono-alt text-amber-300">₹{parseFloat(a.monthly_income||0).toLocaleString("en-IN")}/mo</span>}
                {isOpen
                  ? <MinusCircle className="w-4 h-4 text-slate-400"/>
                  : <PlusCircle className="w-4 h-4 text-slate-400"/>}
              </div>
            </button>
            {isOpen && (
              <div className="p-3 border-t border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ApplField testKey={`a${i}-full_name`} label="Full Name"
                  value={a.full_name} onChange={v => upd(i, "full_name", v)} placeholder="As per PAN"/>
                <ApplField testKey={`a${i}-occupation`} label="Occupation"
                  value={a.occupation} onChange={v => upd(i, "occupation", v)} placeholder="Salaried / Business / Farmer…"/>
                <ApplField testKey={`a${i}-employer`} label="Employer / Business"
                  value={a.employer} onChange={v => upd(i, "employer", v)} placeholder="Company name"/>
                <ApplField testKey={`a${i}-monthly_income`} label="Monthly Income (₹)"
                  type="number" value={a.monthly_income} onChange={v => upd(i, "monthly_income", v)} placeholder="45000"/>
                <ApplField testKey={`a${i}-alt_phone`} label="Alt Phone"
                  value={a.alt_phone} onChange={v => upd(i, "alt_phone", v)} placeholder="Secondary contact"/>
                <ApplField testKey={`a${i}-email`} label="Email" type="email"
                  value={a.email} onChange={v => upd(i, "email", v)} placeholder="name@email.com"/>
                <ApplField testKey={`a${i}-dob`} label="Date of Birth" type="date"
                  value={a.dob} onChange={v => upd(i, "dob", v)}/>
                <ApplField testKey={`a${i}-pan`} label="PAN" value={a.pan}
                  onChange={v => upd(i, "pan", (v || "").toUpperCase())} placeholder="ABCDE1234F"/>
                <div className="sm:col-span-2">
                  <ApplField testKey={`a${i}-address`} label="Address"
                    value={a.address} onChange={v => upd(i, "address", v)}/>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div data-testid="total-combined-income" className="rounded-lg border-2 border-emerald-500/40 bg-emerald-500/10 p-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Total Combined Income</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Sum of Monthly Income across {n} applicant{n>1?"s":""}</div>
        </div>
        <div className="font-display text-3xl text-emerald-300 font-bold" data-testid="total-income-value">
          ₹{totalIncome.toLocaleString("en-IN")}
        </div>
      </div>
    </div>
  );
}

function ApplField({ testKey, label, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
      <Input
        data-testid={`profile-${testKey}`}
        type={type}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-slate-950 border-slate-700 mt-1"
      />
    </div>
  );
}

function emptyApplicant(fullName = "") {
  return { full_name: fullName, occupation: "", employer: "", monthly_income: "", alt_phone: "", email: "", dob: "", pan: "", address: "" };
}

function LocationCaptureSection({ leadId, location, onSaved }) {
  return (
    <LocationCard
      mode="staff"
      leadId={leadId}
      location={location}
      onSaved={onSaved}
    />
  );
}

// -----------------------------------------------------------------------
// RejectLeadDialog — Telecaller rejects an uninterested / ineligible lead.
// Mandatory reason category (dropdown) + detailed reason (min 10 chars).
// -----------------------------------------------------------------------
function RejectLeadDialog({ lead, open, onClose, onRejected }) {
  const [reasons, setReasons] = useState([]);
  const [category, setCategory] = useState("");
  const [detail, setDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategory("");
    setDetail("");
    api.get("/reject-reasons")
      .then(r => setReasons(r.data.categories || []))
      .catch(() => setReasons([
        "Not Interested",
        "Wrong Number / Not Reachable",
        "Ineligible (Income/CIBIL/Age)",
        "Already Has Loan Elsewhere",
        "Duplicate Lead",
        "Language Barrier",
        "Other",
      ]));
  }, [open]);

  const canSubmit = category && detail.trim().length >= 10 && !submitting;

  const submit = async () => {
    if (!canSubmit || !lead) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/leads/${lead.lead_id}/reject`, {
        reason_category: category,
        detailed_reason: detail.trim(),
      });
      toast.success(`Case rejected · ${r.data.rejected_at_ist}`);
      onRejected && onRejected();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="bg-slate-900 border-red-500/40 text-white max-w-md" data-testid="reject-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-red-400 flex items-center gap-2">
            <Ban className="w-4 h-4"/> Reject Case — Why?
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-400 mb-4">
          Rejecting <b className="text-white">{lead?.name}</b>
          <span className="text-slate-500 font-mono-alt"> · {lead?.phone} · {lead?.lead_id}</span>
          <div className="mt-1 text-[11px] text-red-300/80 flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0"/>
            This lead will be removed from your list. Admin can review and restore it later.
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400">
              Reason Category <span className="text-red-400">*</span>
            </Label>
            <select
              data-testid="reject-reason-category"
              value={category}
              onChange={e => setCategory(e.target.value)}
              disabled={submitting}
              className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm focus:border-red-500 focus:outline-none"
            >
              <option value="">Select a reason…</option>
              {reasons.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-400">
              Detailed Reason <span className="text-red-400">*</span>
              <span className="ml-2 text-slate-500 normal-case tracking-normal">(min 10 characters)</span>
            </Label>
            <textarea
              data-testid="reject-detailed-reason"
              rows={4}
              value={detail}
              onChange={e => setDetail(e.target.value)}
              disabled={submitting}
              placeholder="Explain briefly — what did the customer say?"
              className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-red-500"
            />
            <div className={`text-[10px] mt-1 ${detail.trim().length >= 10 ? "text-emerald-400" : "text-slate-500"}`}>
              {detail.trim().length}/10 characters minimum
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            data-testid="reject-cancel-btn"
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            className="text-slate-300 hover:text-white"
          >
            <X className="w-4 h-4 mr-2"/> Cancel
          </Button>
          <Button
            data-testid="reject-confirm-btn"
            onClick={submit}
            disabled={!canSubmit}
            className="bg-red-500 hover:bg-red-400 text-white font-bold rounded-full disabled:opacity-40"
          >
            <Ban className="w-4 h-4 mr-2"/> {submitting ? "Rejecting…" : "Confirm Reject"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDialog({ leadId, name, phone, open, onClose, currentUserId, onSaved, onReject }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [verified, setVerified] = useState(false);
  const [tab, setTab] = useState("form"); // form | chat
  const { districts: locDistricts } = useLocations();
  const [loans, setLoans] = useState([]);
  const [branches, setBranches] = useState([]);
  const [partners, setPartners] = useState([]);
  const { user: authUser } = useAuth();
  const isBankStaff = authUser?.role === "bank_staff";

  useEffect(() => {
    if (!open) return;
    api.get("/loans").then(r => {
      // Defensive frontend dedup: distinct UPPERCASE loan names
      const seen = new Set();
      const uniq = [];
      for (const ln of (r.data || [])) {
        const upper = (ln.name || "").trim().toUpperCase();
        if (!upper || seen.has(upper)) continue;
        seen.add(upper);
        uniq.push({ ...ln, name: upper });
      }
      uniq.sort((a, b) => a.name.localeCompare(b.name));
      setLoans(uniq);
    }).catch(() => setLoans([]));
  }, [open]);

  useEffect(() => {
    if (!open || !leadId) return;
    setTab("form");
    setLoading(true);
    api.get(`/staff/leads/${leadId}/profile`)
      .then(r => {
        const p = r.data.profile || {};
        // Seed the first applicant's full_name from the lead name if it's empty
        if (Array.isArray(p.applicants) && p.applicants.length && !p.applicants[0].full_name) {
          p.applicants[0].full_name = r.data.name || "";
        }
        setProfile(p);
        setVerified(!!r.data.profile_verified);
      })
      .catch(e => { toast.error(errorMessage(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [open, leadId, onClose]);

  const upd = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  // Cascade: when district changes, refresh branch list + clear branch selection
  const currentDistrict = profile?.district || "";
  useEffect(() => {
    if (!currentDistrict || !locDistricts.length) { setBranches([]); return; }
    const d = locDistricts.find(x => (x.name || "").toUpperCase() === currentDistrict.toUpperCase());
    if (!d) { setBranches([]); return; }
    api.get(`/districts/${d.id}/branches`).then(r => setBranches(r.data)).catch(() => setBranches([]));
  }, [currentDistrict, locDistricts]);

  // Load partners in the selected branch
  const currentBranchId = profile?.branch_id || "";
  useEffect(() => {
    if (!currentBranchId) { setPartners([]); return; }
    api.get(`/staff/branches/${currentBranchId}/partners`).then(r => setPartners(r.data)).catch(() => setPartners([]));
  }, [currentBranchId]);

  const titleCase = (s) => (s || "").trim().split(/\s+/).map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : "").join(" ");

  const cleanName = titleCase(profile?.customer_name || "");
  const canSave = !!(cleanName && profile?.loan_product && profile?.district && profile?.branch_id && parseFloat(profile?.amount || 0) > 0 && profile?.lead_source);

  const save = async () => {
    if (!profile?.customer_name?.trim()) return toast.error("Customer Name required");
    if (!profile?.branch_id) return toast.error("Branch required for Partner Assignment");
    if (!(profile?.loan_product && profile?.district && parseFloat(profile?.amount || 0) > 0 && profile?.lead_source)) {
      return toast.error("Loan Product, District, Loan Amount and Lead Source are mandatory");
    }
    if (isLap(profile?.loan_product) && profile?.lap?.co_app_phone && !/^\d{10}$/.test(profile.lap.co_app_phone)) {
      return toast.error("Co-applicant phone must be 10 digits");
    }
    if (isTakeOver(profile?.loan_product) && [profile?.takeover?.co_app_phone, profile?.takeover?.rm_phone].some(p => p && !/^\d{10}$/.test(p))) {
      return toast.error("Take Over phone numbers must be 10 digits");
    }
    setSaving(true);
    try {
      const payload = {
        ...profile,
        customer_name: cleanName,
        amount: parseFloat(profile.amount || 0),
        lap: profile.lap ? { ...profile.lap, calc: { ...computeLap(profile.lap), progress: lapProgress(profile.lap) } } : {},
        takeover: profile.takeover ? { ...profile.takeover, calc: { ...computeTakeover(profile.takeover), progress: takeoverProgress(profile.takeover) } } : {},
      };
      const r = await api.post(`/staff/leads/${leadId}/profile`, payload);
      toast.success("Profile verified & saved");
      setVerified(!!r.data.profile_verified);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSaving(false); }
  };

  const F = ({ k, label, type = "text", placeholder }) => (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
      <Input
        data-testid={`profile-${k}`}
        type={type}
        value={profile?.[k] || ""}
        onChange={e => upd(k, e.target.value)}
        placeholder={placeholder}
        className="bg-slate-950 border-slate-700 mt-1"
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-amber-400 flex items-center gap-2">
            <UserCog className="w-4 h-4"/> Customer Profile — {name}
            <span className="text-xs text-slate-500 font-mono-alt font-normal ml-2">{phone}</span>
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>}
        {!loading && profile && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <button
                type="button"
                onClick={() => setTab("form")}
                data-testid="profile-tab-form"
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${tab === "form" ? "bg-amber-500 text-slate-900 border-amber-400 font-bold" : "text-slate-300 border-slate-700 hover:bg-white/5"}`}
              >Details</button>
              <button
                type="button"
                onClick={() => setTab("chat")}
                data-testid="profile-tab-chat"
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${tab === "chat" ? "bg-amber-500 text-slate-900 border-amber-400 font-bold" : "text-slate-300 border-slate-700 hover:bg-white/5"}`}
              >Chat with Bank</button>
            </div>

            {tab === "form" && (
              <div className="grid md:grid-cols-3 gap-5">
                <div className="md:col-span-2 space-y-5" data-testid="profile-form">
                  {/* CRM-style call timeline for THIS lead (at the top of the form) */}
                  <LeadCallHistory leadId={leadId}/>

                  {/* MANDATORY VERIFIED FIELDS — 7 fields, source of truth */}
                  <div className="rounded-lg border-2 border-amber-500/40 bg-amber-500/5 p-4" data-testid="verified-fields-block">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
                        Verified Fields · 7 Mandatory
                      </div>
                      {verified && (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[9px] uppercase tracking-widest" data-testid="profile-verified-badge">
                          ✓ Verified
                        </Badge>
                      )}
                    </div>

                    {/* 0. Customer Name — full width, top */}
                    <div className="mb-3">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                        <Pencil className="w-3 h-3 text-amber-400"/> Customer Name <span className="text-red-400">*</span>
                        <span className="text-slate-600 text-[9px] normal-case ml-auto">Edit to correct Excel import</span>
                      </Label>
                      <Input
                        data-testid="profile-customer-name"
                        value={profile?.customer_name ?? ""}
                        onChange={e => upd("customer_name", e.target.value)}
                        placeholder="e.g. Ramesh Kumar"
                        className="bg-slate-950 border-amber-500/40 mt-1 h-11"
                      />
                      {profile?.customer_name && cleanName !== profile.customer_name && (
                        <div className="text-[10px] text-slate-500 mt-0.5">Will save as: <span className="text-amber-400 font-semibold">{cleanName}</span></div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* 1. Loan Product */}
                      <div className="sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400">Loan Product <span className="text-red-400">*</span></Label>
                        <select
                          data-testid="profile-loan-product"
                          value={profile?.loan_product || ""}
                          onChange={e => upd("loan_product", e.target.value)}
                          className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:outline-none h-11"
                        >
                          <option value="">Select…</option>
                          {loans.map(ln => <option key={ln.id || ln.name} value={ln.name}>{ln.name}</option>)}
                        </select>
                      </div>
                      {/* 2. District */}
                      <div className="sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400">District <span className="text-red-400">*</span></Label>
                        <select
                          data-testid="profile-district"
                          value={profile?.district || ""}
                          onChange={e => { upd("district", e.target.value); upd("branch_id", ""); upd("branch_name", ""); upd("assigned_partner_id", ""); upd("assigned_partner_name", ""); }}
                          className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:outline-none h-11"
                        >
                          <option value="">Select…</option>
                          {locDistricts.map(d => {
                            const upper = d.name.trim().toUpperCase();
                            return <option key={d.id} value={upper}>{upper}</option>;
                          })}
                        </select>
                      </div>
                      {/* 3. Branch — cascading */}
                      <div className="sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400">Branch <span className="text-red-400">*</span></Label>
                        <select
                          data-testid="profile-branch"
                          value={profile?.branch_id || ""}
                          onChange={e => {
                            const b = branches.find(x => x.id === e.target.value);
                            upd("branch_id", e.target.value);
                            upd("branch_name", b?.branch_name || "");
                            upd("assigned_partner_id", "");
                            upd("assigned_partner_name", "");
                          }}
                          disabled={!currentDistrict}
                          className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:outline-none h-11 disabled:opacity-50"
                        >
                          <option value="">{currentDistrict ? (branches.length ? "Select branch…" : "No branches — ask Admin") : "Pick District first"}</option>
                          {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                      {/* 4. Loan Amount */}
                      <div className="sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400">Loan Amount (₹) <span className="text-red-400">*</span></Label>
                        <Input
                          data-testid="profile-amount"
                          type="number"
                          value={profile?.amount ?? ""}
                          onChange={e => upd("amount", e.target.value)}
                          placeholder="e.g. 500000"
                          className="bg-slate-950 border-slate-700 mt-1 h-11"
                        />
                      </div>
                      {/* 5. Lead Source */}
                      <div className="sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400">Lead Source <span className="text-red-400">*</span></Label>
                        <select
                          data-testid="profile-lead-source"
                          value={profile?.lead_source || "COMPANY_LEAD"}
                          onChange={e => { upd("lead_source", e.target.value); if (e.target.value === "BANK_STAFF_REFERRAL") { upd("assigned_partner_id", ""); upd("keep_direct_company", false); } }}
                          className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:outline-none h-11"
                        >
                          <option value="COMPANY_LEAD">Company Lead</option>
                          {isBankStaff && <option value="BANK_STAFF_REFERRAL">Bank Staff Referral</option>}
                        </select>
                        <div className="text-[9px] text-slate-500 mt-1">{profile?.lead_source === "BANK_STAFF_REFERRAL" ? "Meta / Web / Owner / Referral" : "Meta · Web · Telecaller · Owner · Referral · Walk-in"}</div>
                      </div>
                      {/* 6. Assigned Partner */}
                      <div className="sm:col-span-1">
                        <Label className="text-[10px] uppercase tracking-widest text-slate-400">Assigned Partner</Label>
                        {profile?.lead_source === "BANK_STAFF_REFERRAL" ? (
                          <div
                            data-testid="profile-partner-locked"
                            className="mt-1 h-11 rounded-md bg-slate-950 border border-red-500/40 px-3 flex items-center text-[11px] text-red-300 font-semibold"
                          >
                            Direct Company · Confidential · No DSA
                          </div>
                        ) : (
                          <>
                            <select
                              data-testid="profile-assigned-partner"
                              value={profile?.assigned_partner_id || ""}
                              onChange={e => {
                                const p = partners.find(x => x.id === e.target.value);
                                upd("assigned_partner_id", e.target.value);
                                upd("assigned_partner_name", p?.name || "");
                                if (e.target.value) upd("keep_direct_company", false);
                              }}
                              disabled={!currentBranchId || profile?.keep_direct_company}
                              className="mt-1 w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm focus:border-amber-500 focus:outline-none h-11 disabled:opacity-50"
                            >
                              <option value="">{currentBranchId ? (partners.length ? "Select partner…" : "No partners in branch") : "Pick Branch first"}</option>
                              {partners.map(p => <option key={p.id} value={p.id}>{p.name} · {p.partner_id || "PENDING"}</option>)}
                            </select>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Direct-Company override + commission note */}
                    {profile?.lead_source !== "BANK_STAFF_REFERRAL" && (
                      <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid="keep-direct-company-label">
                        <input
                          type="checkbox"
                          data-testid="profile-keep-direct-company"
                          checked={!!profile?.keep_direct_company}
                          onChange={e => { upd("keep_direct_company", e.target.checked); if (e.target.checked) { upd("assigned_partner_id", ""); upd("assigned_partner_name", ""); } }}
                          className="mt-0.5 accent-amber-500"
                        />
                        <span className="text-[11px] text-slate-300">
                          Keep as <b className="text-amber-400">Direct Company</b> — Full 2% to Company (Owner Handling). Clears any assigned partner.
                        </span>
                      </label>
                    )}

                    <div className="mt-3 rounded-md bg-slate-950/70 border border-white/10 p-2.5 text-[11px] text-slate-300" data-testid="commission-preview">
                      <b className="text-amber-400">Commission Split:</b>{" "}
                      {profile?.lead_source === "BANK_STAFF_REFERRAL" ? (
                        <>1% Bank Staff · 1% Company · <span className="text-red-300 font-semibold">No Partner</span></>
                      ) : profile?.keep_direct_company ? (
                        <>2% Company (Direct — Owner Handling) · <span className="text-slate-400">No Partner</span></>
                      ) : profile?.assigned_partner_id ? (
                        <>1% Partner · 1% Company</>
                      ) : (
                        <>Pick a Partner to auto-split · or check Direct Company for 2%</>
                      )}
                    </div>

                    <div className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                      These 7 fields OVERRIDE any Excel data and become the source of truth everywhere. Customer Name editable for correction. Branch mandatory for auto partner assignment. Bank Staff referral cannot be assigned to DSA.
                    </div>
                  </div>

                  {isLap(profile?.loan_product) && (
                    <LapSections value={profile.lap} onChange={(lap) => setProfile(p => ({ ...p, lap }))} districts={locDistricts} defaultHouseType={defaultHouseTypeFor(profile?.loan_product)} title={(profile?.loan_product || "LAP").trim().toUpperCase()}/>
                  )}
                  {isTakeOver(profile?.loan_product) && (
                    <TakeOverSections value={profile.takeover} onChange={(takeover) => setProfile(p => ({ ...p, takeover }))} districts={locDistricts}/>
                  )}

                  <LocationCaptureSection
                    leadId={leadId}
                    location={profile?.location}
                    onSaved={(loc) => setProfile(p => ({ ...p, location: loc }))}
                  />

                  <div className="space-y-4">
                    <ApplicantsEditor
                      profile={profile}
                      setProfile={setProfile}
                    />
                    <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3">
                      <Label className="text-[10px] uppercase tracking-widest text-slate-500">Internal Notes (private to admin)</Label>
                      <textarea
                        data-testid="profile-notes"
                        value={profile.notes || ""}
                        onChange={e => upd("notes", e.target.value)}
                        rows={3}
                        placeholder="Private admin-only notes…"
                        className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    {profile.updated_at && (
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                        Last edited by {profile.updated_by} · {(() => { try { return new Date(profile.updated_at).toLocaleString("en-IN"); } catch { return profile.updated_at; } })()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-4" data-testid="profile-hints">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-amber-400"/>
                    <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Calling Hints</div>
                  </div>
                  <ol className="space-y-2.5 text-xs text-slate-300 list-decimal list-inside">
                    {CALL_HINTS.map((h, i) => (
                      <li key={i} className="leading-relaxed" data-testid={`hint-${i}`}>{h.replace("{name}", name || "there")}</li>
                    ))}
                  </ol>
                </div>
              </div>
            )}

            {tab === "chat" && <ChatThread leadId={leadId} currentUserId={currentUserId}/>}
          </>
        )}
        <div className="flex justify-between items-center gap-2 mt-4 flex-wrap">
          {tab === "form" && onReject ? (
            <Button
              data-testid="profile-reject-btn"
              onClick={() => onReject({ lead_id: leadId, name, phone })}
              variant="ghost"
              className="text-red-400 hover:text-red-300 border border-red-500/40 hover:bg-red-500/10 rounded-full"
            >
              <Ban className="w-4 h-4 mr-2"/> Reject Case
            </Button>
          ) : <span/>}
          <div className="flex gap-2 ml-auto">
            <Button data-testid="profile-cancel-btn" variant="ghost" onClick={onClose} className="text-slate-300 hover:text-white">
              <X className="w-4 h-4 mr-2"/> Close
            </Button>
            {tab === "form" && (
              <Button data-testid="profile-save-btn" onClick={save} disabled={saving || loading || !canSave} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full disabled:opacity-40">
                <Save className="w-4 h-4 mr-2"/> {saving ? "Saving…" : "Save Profile"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TC_NAV = [
  { key: "home", label: "Home", icon: Target },
  { key: "leads", label: "My Assigned Leads", icon: ClipboardList },
  { key: "in_bank_pool", label: "In Bank Pool", icon: Landmark },
  { key: "moving", label: "Moving Files", icon: FolderSync },
  { key: "bank_requests", label: "Bank Requests", icon: ShieldCheck },
];
const DM_NAV = [
  { key: "leads", label: "District Leads", icon: ClipboardList },
  { key: "team", label: "My Field Team", icon: Users2 },
];

function MyTargetCard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/staff/my-target").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  if (!data.target) {
    return (
      <div className="card-surface p-6 mb-6" data-testid="my-target-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center"><Target className="w-5 h-5 text-slate-500"/></div>
          <div>
            <div className="font-heading text-white">No target set for {data.month}</div>
            <div className="text-sm text-slate-500">Ask your admin to set your monthly target.</div>
          </div>
        </div>
      </div>
    );
  }
  const t = data.target; const a = data.achieved;
  const casePct = Math.min(100, Math.round((a.cases / t.target_cases) * 100));
  const amtPct = Math.min(100, Math.round((a.amount_lakhs / t.target_amount_lakhs) * 100));
  const pending = { cases: Math.max(0, t.target_cases - a.cases), amt: Math.max(0, t.target_amount_lakhs - a.amount_lakhs).toFixed(1) };
  const overall = Math.round((casePct + amtPct) / 2);
  const barColor = overall >= 80 ? "bg-emerald-500" : overall >= 50 ? "bg-amber-500" : "bg-red-500";
  const txt = overall >= 80 ? "text-emerald-400" : overall >= 50 ? "text-amber-400" : "text-red-400";
  return (
    <div className="card-surface p-6 mb-6" data-testid="my-target-card">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center"><Target className="w-5 h-5 text-amber-500"/></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-500 font-bold">My Target · {data.month}</div>
            <div className="font-heading text-lg text-white">{t.target_cases} cases · ₹{t.target_amount_lakhs}L disbursement</div>
          </div>
        </div>
        {t.incentive > 0 && (
          <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2">
            <Trophy className="w-4 h-4 text-amber-400"/>
            <span className="text-sm text-amber-300">Incentive: <span className="font-bold text-white">₹{Number(t.incentive).toLocaleString("en-IN")}</span></span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-950/60 rounded-xl p-4 border border-white/5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Achieved</div>
          <div className="font-display text-2xl text-white mt-1">{a.cases} <span className="text-sm text-slate-400">cases</span></div>
          <div className="text-sm text-emerald-400">₹{a.amount_lakhs}L</div>
        </div>
        <div className="bg-slate-950/60 rounded-xl p-4 border border-white/5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Pending</div>
          <div className="font-display text-2xl text-white mt-1">{pending.cases} <span className="text-sm text-slate-400">cases</span></div>
          <div className="text-sm text-slate-400">₹{pending.amt}L</div>
        </div>
        <div className="bg-slate-950/60 rounded-xl p-4 border border-white/5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Overall Progress</div>
          <div className={`font-display text-3xl mt-1 ${txt}`} data-testid="my-target-pct">{overall}%</div>
        </div>
      </div>
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Cases · {a.cases}/{t.target_cases}</span><span>{casePct}%</span></div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden"><div className={`h-full ${barColor} transition-all`} style={{ width: `${casePct}%` }}/></div>
        <div className="flex justify-between text-xs text-slate-500 mb-1 mt-3"><span>Disbursement · ₹{a.amount_lakhs}L/₹{t.target_amount_lakhs}L</span><span>{amtPct}%</span></div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden"><div className={`h-full ${barColor} transition-all`} style={{ width: `${amtPct}%` }}/></div>
      </div>
    </div>
  );
}

function TelecallerView() {
  const { user } = useAuth();
  const [activeLeads, setActiveLeads] = useState([]);
  const [closedLeads, setClosedLeads] = useState([]);
  const [profileLead, setProfileLead] = useState(null); // {lead_id, name, phone}
  const [timelineLead, setTimelineLead] = useState(null); // full lead doc for viewer
  const [rejectLead, setRejectLead] = useState(null); // {lead_id, name, phone}
  const [tab, setTab] = useState("mine"); // "mine" | "pool" | "closed"
  const [pushLead, setPushLead] = useState(null);
  const [pushChecks, setPushChecks] = useState({ kyc: false, income: false, purpose: false });
  const [pushing, setPushing] = useState(false);

  const loadActive = () => api.get("/staff/leads?state=active").then((r) => setActiveLeads(r.data)).catch((e) => toast.error(errorMessage(e)));
  const loadClosed = () => api.get("/staff/leads?state=closed").then((r) => setClosedLeads(r.data)).catch(() => {});
  useEffect(() => { loadActive(); loadClosed(); }, []);

  const confirmPush = async () => {
    if (!pushLead) return;
    setPushing(true);
    try {
      await api.post(`/staff/leads/${pushLead.lead_id}/push-to-bank`);
      toast.success(`Pushed to bank pool · ${pushLead.district}`);
      setPushLead(null);
      setPushChecks({ kyc: false, income: false, purpose: false });
      loadActive();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setPushing(false); }
  };

  const mineList = activeLeads.filter(l => ["assigned_to_office", "pending_verification"].includes(l.status));
  const poolList = activeLeads.filter(l => ["in_bank_pool", "taken", "ok"].includes(l.status));
  const visible = tab === "mine" ? mineList : tab === "pool" ? poolList : closedLeads;

  const statusBadge = (s) => {
    if (s === "in_bank_pool") return { className: "bg-blue-500/20 text-blue-300 border-blue-500/30", label: "In Bank Pool" };
    if (s === "taken")        return { className: "bg-amber-500/20 text-amber-300 border-amber-500/30", label: "Bank Reviewing" };
    if (s === "ok")           return { className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", label: "Approved · Awaiting Disbursal" };
    if (s === "disbursed")    return { className: "bg-emerald-600/20 text-emerald-300 border-emerald-500/40", label: "Disbursed 🎉" };
    if (s === "rejected")     return { className: "bg-red-500/20 text-red-300 border-red-500/30", label: "Rejected" };
    if (s === "returned")     return { className: "bg-slate-500/20 text-slate-300 border-slate-500/30", label: "Returned" };
    return { className: "bg-slate-700/40 text-slate-300 border-slate-600", label: s || "Pending" };
  };

  const allChecked = pushChecks.kyc && pushChecks.income && pushChecks.purpose;

  const PILLS = [
    { key: "mine",   label: "My Leads",     icon: ClipboardList, count: mineList.length },
    { key: "pool",   label: "In Bank Pool", icon: Landmark,      count: poolList.length },
    { key: "closed", label: "Closed",       icon: CheckCircle2,  count: closedLeads.length },
  ];

  return (
    <PullToRefresh onRefresh={async () => { await loadActive(); await loadClosed(); }} testId="ptr-office-leads">
    <div data-testid="office-leads-grid" className="pb-6">
      <TelecallerCallStats/>
      <OverdueFollowupBanner
        onOpenProfile={(lead) => setProfileLead(lead)}
      />
      {/* Instagram-style filter pills — horizontal scroll on mobile */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 mb-4 md:flex-wrap md:overflow-visible">
        {PILLS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-colors ${
              tab === t.key
                ? "bg-amber-400 text-slate-900 border-amber-400 font-bold"
                : "bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800"
            }`}
          >
            <t.icon className="w-3.5 h-3.5"/>
            <span>{t.label}</span>
            <span className={`text-[10px] rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center ${tab === t.key ? "bg-slate-900/20 text-slate-900" : "bg-slate-800 text-slate-400"}`}>{t.count}</span>
          </button>
        ))}
      </div>

      <div className="grid gap-3 md:gap-4 md:grid-cols-2 lg:grid-cols-3">
        {visible.length === 0 && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-10 col-span-full text-center text-slate-500 text-sm" data-testid="empty-state">
            {tab === "mine" ? "No pending leads. Everything in-flight is with a bank." :
             tab === "pool" ? "Nothing in the bank pool yet. Verify a lead and push it." :
             "No closed leads yet. Disbursed and rejected ones will land here."}
          </div>
        )}
        {visible.map((l) => {
          const inPool = ["in_bank_pool", "taken", "ok"].includes(l.status);
          const isClosed = ["disbursed", "rejected", "returned"].includes(l.status);
          const sb = statusBadge(l.status);
          const verified = !!l.profile_verified;
          const handleWA = () => { window.open(`https://wa.me/91${l.phone}`, "_blank", "noopener,noreferrer"); };
          const card = (
            <div
              className={`rounded-2xl border border-slate-800 bg-slate-900 p-4 ${isClosed ? "opacity-80" : ""}`}
              style={{ marginBottom: 0 }}
              data-testid={`lead-${l.lead_id}`}>
              {/* Row 1: ID · status */}
              <div className="flex items-center justify-between gap-2">
                <div className="font-mono-alt text-amber-400 text-xs">{l.lead_id}</div>
                <Badge className={sb.className + " text-[9px] uppercase tracking-widest whitespace-nowrap"} data-testid={`status-${l.lead_id}`}>{sb.label}</Badge>
              </div>

              {/* Row 2: Name (big white bold) */}
              <div className="mt-1 font-heading text-xl text-white leading-tight" data-testid={`name-${l.lead_id}`}>{l.name || "Unknown"}</div>

              {/* Tracked call block: masked phone + big pulse CALL button + last-call preview */}
              {!isClosed && <TrackedCallButton lead={l}/>}
              {isClosed && (
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-400" data-testid={`phone-closed-${l.lead_id}`}>
                  <Phone className="w-3.5 h-3.5 text-emerald-400"/>
                  <span className="font-mono-alt select-all">{l.phone || "—"}</span>
                </div>
              )}

              {/* Row 4: Verified summary OR warning */}
              {verified ? (
                <div className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs flex items-center gap-2 flex-wrap">
                  <LoanIcon loan={{ name: l.loan_type }} className="w-3.5 h-3.5 text-emerald-400"/>
                  <span className="text-emerald-100 font-semibold">{l.loan_type}</span>
                  <span className="text-emerald-300/80">· ₹{parseFloat(l.amount || 0).toLocaleString("en-IN")}</span>
                  <span className="text-emerald-300/80 flex items-center gap-1"><MapPin className="w-3 h-3"/>{l.district}</span>
                </div>
              ) : (
                <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100 flex items-start gap-2" data-testid={`not-verified-${l.lead_id}`}>
                  <UserCog className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5"/>
                  <span>Not Verified — open <span className="font-semibold">Profile</span> to set Loan Product, District & Amount.</span>
                </div>
              )}

              {/* Bank owner line for in-pool/closed cases */}
              {(inPool || isClosed) && l.bank_name && (
                <div className="mt-2 flex items-center gap-2 text-blue-300 text-xs" data-testid={`bank-${l.lead_id}`}>
                  <Landmark className="w-3 h-3"/>
                  <span><span className="text-slate-500">Bank:</span> <b className="text-white">{l.bank_name}</b>
                    {l.bank_staff_name && <span className="text-slate-500"> · {l.bank_staff_name}</span>}
                  </span>
                </div>
              )}
              {(inPool && !l.bank_name) && (
                <div className="mt-2 text-[11px] text-slate-500 italic">Waiting for a bank staff to pick this up.</div>
              )}

              {/* Rejection history (compact) */}
              {l.rejections && l.rejections.length > 0 && (
                <RejectionHistory lead={l} compact/>
              )}

              {/* Action pills row: WA / Profile / Timeline (Call is above via TrackedCallButton) */}
              <div className="mt-3 grid grid-cols-3 gap-2">
                <a href={`https://wa.me/91${l.phone}`} target="_blank" rel="noreferrer"
                   className="min-h-[44px] flex items-center justify-center gap-1 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full hover:bg-emerald-500/20 active:scale-95 transition"
                   data-testid={`wa-btn-${l.lead_id}`}>
                  <MessageSquare className="w-3.5 h-3.5"/><span className="hidden sm:inline">WhatsApp</span>
                </a>
                <button
                  type="button"
                  onClick={() => setProfileLead({ lead_id: l.lead_id, name: l.name, phone: l.phone })}
                  data-testid={`profile-btn-${l.lead_id}`}
                  className="min-h-[44px] flex items-center justify-center gap-1 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full hover:bg-amber-500/20 active:scale-95 transition">
                  <UserCog className="w-3.5 h-3.5"/><span className="hidden sm:inline">Profile</span>
                </button>
                <button
                  type="button"
                  onClick={() => setTimelineLead(l)}
                  data-testid={`timeline-btn-${l.lead_id}`}
                  className="min-h-[44px] flex items-center justify-center gap-1 text-xs text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-full hover:bg-blue-500/20 active:scale-95 transition">
                  <GitBranch className="w-3.5 h-3.5"/><span className="hidden sm:inline">Timeline</span>
                </button>
              </div>

              {/* Bottom CTA */}
              {!inPool && !isClosed && (
                verified ? (
                  <Button onClick={() => setPushLead(l)} data-testid={`push-${l.lead_id}`}
                    className="mt-3 w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full">
                    <CheckCircle2 className="w-4 h-4 mr-2"/> Move to Bank Pool
                  </Button>
                ) : (
                  <Button
                    onClick={() => setProfileLead({ lead_id: l.lead_id, name: l.name, phone: l.phone })}
                    data-testid={`push-${l.lead_id}`}
                    className="mt-3 w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                    <UserCog className="w-4 h-4 mr-2"/> Verify Profile First
                  </Button>
                )
              )}
              {inPool && (
                <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500 text-center py-2.5 border border-white/5 rounded-full">
                  Collaborating with bank · Add notes anytime
                </div>
              )}

              {/* Reject Case — only for leads still in the telecaller's hands
                  (mine list: assigned_to_office / pending_verification). Once
                  pushed to the bank pool the case is out of the telecaller's
                  control and cannot be rejected. */}
              {!inPool && !isClosed && (
                <button
                  type="button"
                  onClick={() => setRejectLead({ lead_id: l.lead_id, name: l.name, phone: l.phone })}
                  data-testid={`reject-btn-${l.lead_id}`}
                  className="mt-2 w-full h-10 flex items-center justify-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 text-red-300 text-xs font-bold uppercase tracking-widest hover:bg-red-500/20 active:scale-95 transition">
                  <Ban className="w-3.5 h-3.5"/> Reject Case
                </button>
              )}
              {isClosed && (
                <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500 text-center py-2.5 border border-white/5 rounded-full">
                  Case closed · Read-only reference
                </div>
              )}
            </div>
          );
          // Only wrap "mine" cards in swipe for WhatsApp — call is now tracked-only
          if (tab === "mine" && !isClosed) {
            return (
              <SwipeableLeadCard key={l.lead_id} leadId={l.lead_id}
                onSwipeLeft={handleWA}>
                {card}
              </SwipeableLeadCard>
            );
          }
          return <div key={l.lead_id}>{card}</div>;
        })}
      </div>

      <ProfileDialog
        leadId={profileLead?.lead_id}
        name={profileLead?.name}
        phone={profileLead?.phone}
        open={!!profileLead}
        onClose={() => setProfileLead(null)}
        currentUserId={user?.id}
        onSaved={loadActive}
        onReject={(lead) => { setProfileLead(null); setRejectLead(lead); }}
      />

      {/* Reject-case dialog — mandatory reason category + detailed reason */}
      <RejectLeadDialog
        lead={rejectLead}
        open={!!rejectLead}
        onClose={() => setRejectLead(null)}
        onRejected={() => { loadActive(); loadClosed(); }}
      />

      {/* Timeline dialog (view-only) — 9-stage pipeline + rejection history */}
      <Dialog open={!!timelineLead} onOpenChange={(o) => !o && setTimelineLead(null)}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="timeline-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-blue-300 flex items-center gap-2">
              <GitBranch className="w-4 h-4"/> Pipeline — {timelineLead?.lead_id}
            </DialogTitle>
          </DialogHeader>
          {timelineLead && (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                <span className="text-white font-semibold">{timelineLead.name}</span>
                <span className="font-mono-alt"> · {timelineLead.phone}</span>
              </div>
              {timelineLead.rejections && timelineLead.rejections.length > 0 && (
                <RejectionHistory lead={timelineLead}/>
              )}
              <div className="rounded-lg border border-white/5 bg-slate-950/40 p-3">
                <PipelineTimeline lead={timelineLead} compact/>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Push confirmation checklist */}
      <Dialog open={!!pushLead} onOpenChange={(o) => !o && setPushLead(null)}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md" data-testid="push-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-400 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4"/> Verify before pushing
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-400 mb-3">
            Push <b className="text-white">{pushLead?.name}</b> to the Bank Pool as
            <span className="text-white font-mono-alt mx-1">{pushLead?.loan_type}</span> ·
            <span className="text-white font-mono-alt mx-1">{pushLead?.district}</span> ·
            <span className="text-white font-mono-alt mx-1">₹{parseFloat(pushLead?.amount || 0).toLocaleString("en-IN")}</span>.
            All three checks must be ticked.
          </div>

          <div className="space-y-2" data-testid="push-checklist">
            {[
              ["kyc",     "KYC ready — PAN, Aadhaar, 3-month bank statement collected"],
              ["income",  "Income confirmed — payslip / ITR / business proof matches profile"],
              ["purpose", "Loan purpose confirmed — customer is genuinely interested"],
            ].map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-white/5">
                <input
                  type="checkbox"
                  checked={pushChecks[k]}
                  onChange={e => setPushChecks(p => ({ ...p, [k]: e.target.checked }))}
                  className="accent-amber-500 w-4 h-4 mt-1"
                  data-testid={`push-check-${k}`}
                />
                <span className="text-sm text-slate-200">{label}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button data-testid="push-cancel-btn" variant="ghost" onClick={() => { setPushLead(null); setPushChecks({ kyc: false, income: false, purpose: false }); }} className="text-slate-300 hover:text-white">Cancel</Button>
            <Button
              data-testid="push-confirm-btn"
              onClick={confirmPush}
              disabled={!allChecked || pushing}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full disabled:opacity-40"
            >
              <CheckCircle2 className="w-4 h-4 mr-2"/> {pushing ? "Pushing…" : "Push to Bank Pool"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
  );
}

function DMTeamView() {
  const [data, setData] = useState({ district: null, field_staff: [] });
  useEffect(() => { api.get("/dm/team").then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  return (
    <div className="card-surface p-6" data-testid="dm-team-page">
      <div className="mb-6">
        <div className="font-heading text-xl text-white">My Field Team · {data.district || "—"}</div>
        <div className="text-sm text-slate-500">Only Field staff in your district. Telecallers are central and not managed here.</div>
      </div>
      <Table>
        <TableHeader><TableRow className="border-white/5"><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Salary</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.field_staff.map((s) => (
            <TableRow key={s.id} className="border-white/5" data-testid={`dm-field-${s.id}`}>
              <TableCell className="text-white">{s.name}</TableCell>
              <TableCell className="font-mono-alt">{s.phone}</TableCell>
              <TableCell className="font-mono-alt text-slate-300">{s.monthly_salary ? `₹${Number(s.monthly_salary).toLocaleString("en-IN")}` : "—"}</TableCell>
              <TableCell>
                <Badge className={
                  s.status === "terminated" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                  s.status === "suspended" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                  "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                }>{s.status || "active"}</Badge>
              </TableCell>
            </TableRow>
          ))}
          {data.field_staff.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No field staff in your district yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function DMLeadsView() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/dm/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  const statusColor = (s) => ({
    pending_verification: "bg-slate-700 text-slate-200 border-slate-600",
    assigned_to_office: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    in_bank_pool: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    taken: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    ok: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    disbursed: "bg-emerald-500/40 text-emerald-300 border-emerald-500/40",
  }[s] || "bg-slate-700 text-slate-200");
  return (
    <div className="card-surface p-6" data-testid="dm-leads-page">
      <div className="mb-6">
        <div className="font-heading text-xl text-white">All Leads in My District</div>
        <div className="text-sm text-slate-500">Every lead in your district across all stages.</div>
      </div>
      <Table>
        <TableHeader><TableRow className="border-white/5"><TableHead>ID</TableHead><TableHead>Name</TableHead><TableHead>Loan</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {leads.map((l) => (
            <TableRow key={l.lead_id} className="border-white/5">
              <TableCell className="font-mono-alt text-amber-400">{l.lead_id}</TableCell>
              <TableCell className="text-white">{l.name}</TableCell>
              <TableCell>{l.loan_type}</TableCell>
              <TableCell>₹{parseFloat(l.amount).toLocaleString("en-IN")}</TableCell>
              <TableCell><Badge className={statusColor(l.status)}>{l.status}</Badge></TableCell>
            </TableRow>
          ))}
          {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No leads in your district yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------- Bank Assignment Requests (Telecaller Controls PII) -----
function BankAssignmentRequestsView() {
  const [requests, setRequests] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [selected, setSelected] = useState({}); // { lead_id: [staff_id, ...] }
  const [submittingFor, setSubmittingFor] = useState(null);

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/staff/bank-requests"),
        api.get("/staff/bank-staff-directory"),
      ]);
      setRequests(r.data.requests || []);
      setDirectory(s.data.staff || []);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  // Single-select: telecaller picks ONE staff at a time. Others remain WAITING.
  const toggle = (leadId, staffId) => {
    setSelected(prev => {
      const cur = prev[leadId] || [];
      const isSame = cur.length === 1 && cur[0] === staffId;
      return { ...prev, [leadId]: isSame ? [] : [staffId] };
    });
  };

  const approve = async (leadId) => {
    const ids = selected[leadId] || [];
    if (!ids.length) { toast.error("Pick at least one bank staff to assign"); return; }
    setSubmittingFor(leadId);
    try {
      const res = await api.post(`/staff/bank-requests/${leadId}/assign`, { staff_ids: ids });
      toast.success(`Assigned to: ${res.data.assigned_names}`);
      setSelected(prev => { const cp = { ...prev }; delete cp[leadId]; return cp; });
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmittingFor(null); }
  };

  const maskName = (n) => {
    if (!n) return "—";
    return n.split(" ").map(w => (w ? w[0] + "*".repeat(Math.max(1, w.length-1)) : "")).join(" ");
  };
  const fmtTs = (iso) => { try { return new Date(iso).toLocaleString("en-IN", {day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}); } catch { return iso || ""; } };

  return (
    <div className="card-surface p-6" data-testid="bank-requests-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-heading text-xl text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400"/> Bank Assignment Requests
          </div>
          <div className="text-xs text-slate-500 mt-1">
            You control who can view customer details and take each case. Pick ONE bank staff who requested and approve. Others stay in WAITING.
          </div>
        </div>
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">{requests.length} pending</Badge>
      </div>
      {requests.length === 0 && (
        <div className="text-center py-16 text-slate-500 text-sm" data-testid="bank-requests-empty">
          No pending bank assignment requests right now.
        </div>
      )}
      <div className="space-y-4">
        {requests.map((l) => {
          const sel = selected[l.lead_id] || [];
          const reqs = l.assignment_requests || [];
          return (
            <div key={l.lead_id} className="rounded-lg border border-white/5 bg-slate-950/50 p-4" data-testid={`bank-request-${l.lead_id}`}>
              <div className="flex flex-wrap gap-4 justify-between items-start">
                <div className="min-w-0 flex-1">
                  <div className="font-mono-alt text-amber-400 text-sm">{l.lead_id}</div>
                  <div className="mt-1 flex items-center gap-2 text-white">
                    <span className="font-heading" data-testid={`req-cust-masked-${l.lead_id}`}>{maskName(l.name)}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-widest">(masked in list)</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                    <div className="flex items-center gap-1.5"><IndianRupee className="w-3 h-3"/> {parseFloat(l.amount).toLocaleString("en-IN")} · <b className="text-slate-300">{l.loan_type}</b></div>
                    <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3"/> {l.district}</div>
                    <div className="flex items-center gap-1.5 text-amber-300"><Users className="w-3 h-3"/> Needs <b>{l.required_staff_count || 1}</b> staff</div>
                  </div>
                </div>
              </div>

              {/* Requesting bank staff list */}
              <div className="mt-4 rounded-md border border-white/5 bg-slate-900/60 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Requested by</div>
                <div className="space-y-1.5">
                  {reqs.length === 0 && <div className="text-xs text-slate-500 italic">No requests recorded.</div>}
                  {reqs.map((r, i) => (
                    <div key={i} className="text-xs text-slate-300 flex flex-wrap items-center gap-x-2" data-testid={`req-item-${l.lead_id}-${i}`}>
                      <Hourglass className="w-3 h-3 text-amber-400"/>
                      <span className="font-semibold text-white">{r.staff_name || "?"}</span>
                      {r.bank_name && <span className="text-slate-400"><Building2 className="inline w-3 h-3 mr-0.5"/>{r.bank_name}</span>}
                      <span className="text-slate-500">· needs {r.required_count || 1}</span>
                      <span className="text-slate-500 ml-auto">{fmtTs(r.requested_at)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Assignment picker — restricted to staff who ACTUALLY requested this lead */}
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Pick bank staff to assign (single-select)</div>
                {(() => {
                  const requestedIds = reqs.map(r => r.staff_id).filter(Boolean);
                  const reqByStaff = Object.fromEntries(reqs.map(r => [r.staff_id, r]));
                  const eligible = directory.filter(s => requestedIds.includes(s.id));
                  if (eligible.length === 0) {
                    return (
                      <div className="text-xs text-slate-500 italic px-2 py-3 border border-dashed border-slate-800 rounded"
                           data-testid={`assign-empty-${l.lead_id}`}>
                        No bank staff requested this lead yet — waiting for requests.
                      </div>
                    );
                  }
                  return (
                    <>
                      <div className="grid gap-1.5 max-h-52 overflow-y-auto pr-1">
                        {eligible.map(s => {
                          const isSel = sel.includes(s.id);
                          const req = reqByStaff[s.id] || {};
                          return (
                            <label key={s.id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer border ${isSel ? "border-emerald-500/50 bg-emerald-500/10" : "border-slate-800 bg-slate-900/40 hover:bg-slate-800/50"}`}
                                   data-testid={`assign-choice-${l.lead_id}-${s.id}`}>
                              <input type="radio" name={`assign-${l.lead_id}`} checked={isSel} onChange={() => toggle(l.lead_id, s.id)} className="accent-emerald-400"/>
                              <span className="text-white font-semibold">{s.name || "—"}</span>
                              {(req.bank_name || s.bank_name) && <span className="text-slate-400">· {req.bank_name || s.bank_name}</span>}
                              <span className="text-slate-500">· needs {req.required_count || 1}</span>
                              {s.district && <span className="text-slate-500 ml-auto text-[10px] uppercase tracking-widest">{s.district}</span>}
                            </label>
                          );
                        })}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="text-[11px] text-slate-500">
                          Selected: <span className="text-white font-bold">{sel.length}</span>
                          {l.required_staff_count ? <span> / needs {l.required_staff_count}</span> : null}
                        </div>
                        <Button
                          data-testid={`approve-assign-${l.lead_id}`}
                          disabled={submittingFor === l.lead_id || sel.length === 0}
                          onClick={() => approve(l.lead_id)}
                          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full disabled:opacity-40">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1.5"/> Approve & Assign
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TelecallerStatsStrip() {
  const [stats, setStats] = useState([]);
  useEffect(() => {
    api.get("/staff/leads?state=active").then(({ data }) => {
      const active = data || [];
      setStats([
        { key: "total",    label: "Total Assigned", value: active.length,
          tone: "border-amber-500/40 bg-amber-500/10 text-amber-100" },
        { key: "pool",     label: "In Bank Pool",   value: active.filter(l => ["in_bank_pool","taken","ok"].includes(l.status)).length,
          tone: "border-blue-500/40 bg-blue-500/10 text-blue-100" },
        { key: "verified", label: "Verified",       value: active.filter(l => l.profile_verified && !["in_bank_pool","taken","ok","disbursed","rejected"].includes(l.status)).length,
          tone: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100" },
        { key: "closed",   label: "Disbursed",      value: active.filter(l => l.status === "disbursed").length,
          tone: "border-slate-600/50 bg-slate-800/60 text-slate-100" },
      ]);
    }).catch(() => {});
  }, []);
  return stats;
}

export default function OfficeStaffDashboard() {
  const { user } = useAuth();
  const isDM = user?.sub_role === "district_manager";
  const [active, setActive] = useState("home");
  const stats = TelecallerStatsStrip();

  const triggerAdd = () => {
    const btn = document.querySelector('[data-testid="add-customer-btn"]');
    if (btn) btn.click();
  };
  const triggerReferral = () => {
    const btn = document.querySelector('[data-testid="add-referral-btn"]');
    if (btn) btn.click();
  };

  if (isDM) {
    return (
      <DashLayout title="Mahalir Kuzhu Manager" subtitle={user?.district || user?.managed_district || "District"} testId="dm" nav={DM_NAV} activeKey={active} onNav={setActive}>
        <MyTargetCard/>
        <UniversalLeadAdd/>
        <ReferralButton/>
        {active === "leads" && <DMLeadsView />}
        {active === "team" && <DMTeamView />}
      </DashLayout>
    );
  }
  return (
    <CallTrackerProvider>
    <OfficeStaffShell
      nav={TC_NAV}
      activeKey={active}
      onNav={setActive}
      onAddCustomer={triggerAdd}
      onReferral={triggerReferral}
      stats={stats}
    >
      {/* Hidden triggers — clicked programmatically by the shell FAB + banner */}
      <div className="sr-only" aria-hidden="true">
        <UniversalLeadAdd/>
        <ReferralButton/>
      </div>

      {active === "home" && <TelecallerHomeDashboard />}
      {active === "leads" && <TelecallerView />}
      {active === "in_bank_pool" && <InBankPoolPage />}
      {active === "moving" && <MovingFilesView />}
      {active === "bank_requests" && <BankAssignmentRequestsView />}
    </OfficeStaffShell>
    </CallTrackerProvider>
  );
}
